import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { Button } from "@/components/ui/button"
import { ArrowRight, MapPin, Calendar, Database, ShieldCheck, Zap } from "lucide-react"
import { Link } from "react-router-dom"
import { ces2026FAQs } from "@/data/faqData"

const RSAatCES2026 = () => {
    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
            <SEO
                title="RSA Tech Group @ CES 2026 | A Defining Moment"
                description="CES 2026 was a signal that enterprises are moving to execution. RSA Tech Group engaged global leaders to shape intelligent, human-centered systems."
                image="/images/CES 2026 Images/IMG_6287.JPG"
            />
            <Navigation />

            {/* ───── S1 Hero - Dark ───── */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 z-0">
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover opacity-75"
                    >
                        <source src="/images/CES 2026 Images/eea8bbb0-8061-429f-8653-6466eefa8e1c.mov" type="video/quicktime" />
                        <source src="/images/CES 2026 Images/eea8bbb0-8061-429f-8653-6466eefa8e1c.mov" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0f1629]/80 via-[#0f1629]/70 to-[#0f1629]" />
                </div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_40%,rgba(253,164,0,0.08),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#FDA400]/[0.06] rounded-full blur-[120px]" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#1C2B6E]/20 rounded-full blur-[100px]" />

                <div className="container relative z-10 px-4 max-w-5xl mx-auto text-center">
                    <span className="text-[#FDA400] uppercase tracking-[0.2em] text-sm font-bold mb-6 block">CES 2026 Recap</span>

                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-none mb-6 text-white">
                        <span className="text-[#FDA400]">CES 2026</span>
                        <span className="block text-3xl md:text-5xl mt-4 font-light text-slate-200 tracking-normal font-sans">
                            A Defining Moment for the <br className="hidden md:block" />
                            <span className="font-medium text-white">Cognitive Enterprise</span>
                        </span>
                    </h1>

                    <div className="inline-flex items-center gap-4 px-6 py-3 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-sm mb-10">
                        <span className="flex items-center gap-2 text-slate-300 text-sm font-medium"><MapPin className="w-4 h-4 text-[#FDA400]" /> Las Vegas, NV</span>
                        <span className="w-1 h-1 rounded-full bg-white/30" />
                        <span className="flex items-center gap-2 text-slate-300 text-sm font-medium"><Calendar className="w-4 h-4 text-[#FDA400]" /> Jan 6-9, 2026</span>
                    </div>

                    <p className="text-xl md:text-2xl text-slate-300 font-medium mb-12 leading-relaxed max-w-3xl mx-auto">
                        CES 2026 was more than a technology showcase. It was a clear signal that enterprises are moving from experimentation to execution. RSA Tech Group engaged with global leaders to shape how intelligent, human-centered systems are designed, governed, and operated at scale.
                    </p>

                    <Link to="/contact">
                        <Button className="h-auto px-12 py-5 text-lg font-bold rounded-full bg-[#FDA400] text-[#1C2B6E] hover:bg-[#FDA400]/90 transition-all shadow-2xl shadow-[#FDA400]/20 hover:shadow-[#FDA400]/30 hover:scale-105">
                            Partner with RSA Tech Group
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* ───── S2 CES at a Glance - Light + Dot Grid ───── */}
            <section className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.35]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />

                <div className="container px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                        <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-200 group">
                            <img
                                src="/images/CES 2026 Images/IMG_6236.jpg"
                                alt="CES 2026 Event Floor"
                                className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-[2px] bg-[#FDA400]" />
                                <span className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.15em]">Overview</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-bold leading-tight text-[#1C2B6E]">CES 2026 at a Glance</h2>
                            <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                                <p>
                                    CES 2026 brought together the largest and most diverse global technology ecosystem under one roof. The event hosted over <strong className="text-[#1C2B6E]">4,100+ exhibitors</strong>, welcomed more than <strong className="text-[#1C2B6E]">145,000+ attendees</strong>, and spanned multiple venues across Las Vegas.
                                </p>
                                <p>
                                    This year's conference marked a clear shift in focus. The dominant conversation was about making intelligence operational, secure, and dependable inside complex enterprise environments.
                                </p>
                                <p>
                                    The strongest momentum centered around enterprise-scale AI adoption, automation reliability, data sovereignty, and the convergence of physical and digital systems.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───── S3 Why CES Mattered - Dark ───── */}
            <section className="relative py-24 bg-[#0f1629] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.04),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="container px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-[2px] bg-[#FDA400]" />
                                <span className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.15em]">Why It Mattered</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-bold text-white">Why CES 2026 Mattered</h2>
                            <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
                                <p>
                                    For RSA Tech Group, CES 2026 validated what we see across enterprise environments every day. Innovation is no longer constrained by technology availability. The constraint is execution. Organizations are seeking partners who can architect, deploy, and operate intelligent systems with accountability.
                                </p>
                            </div>
                            <div className="p-6 bg-white/[0.04] border border-white/[0.08] rounded-2xl backdrop-blur-sm">
                                <p className="text-white font-semibold text-lg leading-relaxed">
                                    This event confirmed that the future belongs to enterprises that treat AI and automation as long-term operational capabilities, not one-time initiatives.
                                </p>
                            </div>
                        </div>

                        <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/[0.08]">
                            <img
                                src="/images/CES 2026 Images/IMG_6287.JPG"
                                alt="CES 2026 Tech Display"
                                className="w-full h-auto object-cover transform transition-transform duration-700 hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-[#0f1629]/20" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ───── S4 Strategic Engagements - Light + Dot Grid ───── */}
            <section className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.35]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />

                <div className="container px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                        <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-200 group">
                            <img
                                src="/images/CES 2026 Images/4715a95d-8164-4517-8aba-e28f4ad770d0.jpeg"
                                alt="CES Strategic Engagements"
                                className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-[2px] bg-[#FDA400]" />
                                <span className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.15em]">Engagements</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-bold leading-tight text-[#1C2B6E]">Strategic Engagements on the Ground</h2>
                            <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                                <p>
                                    Beyond demonstrations and product showcases, our team engaged in architecture-level discussions with enterprise leaders. These conversations focused on aligning technology investments with measurable business outcomes and long-term operational stability.
                                </p>
                                <p>
                                    We participated in solution mapping sessions, executive roundtables, and forward-looking discussions on how managed execution models are replacing fragmented delivery approaches.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───── S5 What We Validated - Dark ───── */}
            <section className="relative py-24 bg-[#0f1629] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.04),transparent_70%)]" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#1C2B6E]/20 rounded-full blur-[100px]" />

                <div className="container px-4 relative z-10">
                    <div className="text-center mb-16 max-w-4xl mx-auto">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-8 h-[2px] bg-[#FDA400]" />
                            <span className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.15em]">Insights</span>
                            <div className="w-8 h-[2px] bg-[#FDA400]" />
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold text-white">What We Validated at CES</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {[
                            {
                                icon: Database,
                                title: "The Integration Gap",
                                desc: "Enterprises are investing heavily in AI tools, but many lack unified architectures that allow these systems to work together reliably."
                            },
                            {
                                icon: ShieldCheck,
                                title: "Trust and Governance",
                                desc: "Security, compliance, and data ownership remain decisive factors. Organizations want partners who take responsibility beyond implementation."
                            },
                            {
                                icon: Zap,
                                title: "Execution Urgency",
                                desc: "Leaders are under pressure to move from pilots to production without increasing risk or operational burden."
                            }
                        ].map((item, i) => (
                            <div key={i} className="p-8 rounded-3xl bg-white/[0.04] border border-white/[0.08] hover:border-[#FDA400]/30 transition-all duration-300 hover:-translate-y-1 group">
                                <item.icon className="w-8 h-8 text-[#FDA400] mb-6" />
                                <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───── S6 Industry Conversations - Light + Dot Grid ───── */}
            <section className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.35]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />

                <div className="container px-4 relative z-10">
                    <div className="text-center mb-16 max-w-4xl mx-auto">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-8 h-[2px] bg-[#FDA400]" />
                            <span className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.15em]">Industries</span>
                            <div className="w-8 h-[2px] bg-[#FDA400]" />
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#1C2B6E]">Industry Conversations We Led</h2>
                        <p className="text-slate-500 text-lg max-w-3xl mx-auto">
                            At CES 2026, RSA Tech Group connected with organizations across multiple industries that are actively modernizing their core systems.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {[
                            "High-reliability electronic component manufacturing",
                            "Telecommunications infrastructure and network services",
                            "Biotechnology and medical hardware systems",
                            "Robotics and intelligent automation platforms",
                            "Healthcare systems and digital care environments"
                        ].map((text, i) => (
                            <div key={i} className={`partner-card rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group ${i === 0 ? 'md:col-span-2' : ''}`}>
                                <h3 className="text-xl font-bold text-[#1C2B6E] leading-snug">{text}</h3>
                            </div>
                        ))}
                    </div>

                    <p className="text-center mt-12 text-slate-500 text-lg font-medium">
                        Each discussion centered on building <strong className="text-[#1C2B6E]">resilient, secure, and scalable</strong> enterprise foundations.
                    </p>
                </div>
            </section>

            {/* ───── S7 Closing CTA - Dark ───── */}
            <section className="relative py-24 bg-[#0f1629] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.04),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="container px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/[0.08] group">
                            <img
                                src="/images/CES 2026 Images/17161D524C43456D15100F54544941761F151C185044477912.jpg"
                                alt="RSA Tech Group Leadership"
                                className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-[#0f1629]/20" />
                        </div>

                        <div>
                            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tight leading-tight text-white">
                                CES Was a Milestone. <br />
                                <span className="text-[#FDA400]">Execution is the Journey.</span>
                            </h2>
                            <p className="text-xl text-slate-300 font-medium mb-8 leading-relaxed">
                                The future of the cognitive enterprise is not built on experimentation alone. It is built through disciplined execution, trusted partnerships, and systems designed to evolve.
                            </p>
                            <p className="text-lg text-slate-400 mb-10 border-l-4 border-[#FDA400] pl-6">
                                RSA Tech Group is ready to help enterprises move forward with confidence.
                            </p>

                            <div className="flex flex-col sm:flex-row items-start gap-6">
                                <Link to="/contact">
                                    <Button className="h-auto px-10 py-5 text-lg font-bold rounded-full bg-[#FDA400] text-[#1C2B6E] hover:bg-[#FDA400]/90 transition-all shadow-2xl shadow-[#FDA400]/20 hover:shadow-[#FDA400]/30 hover:scale-105">
                                        Partner with RSA Tech Group <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Link to="/what-we-do" className="inline-flex items-center gap-2 text-[#FDA400] font-bold hover:text-[#FDA400]/80 transition-colors group py-3">
                                    Explore Our Services
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───── S8 FAQ - Light ───── */}
            <PageFAQ
                faqs={ces2026FAQs}
                title="CES 2026 FAQs"
                subtitle="Common questions about RSA Tech Group at CES 2026"
                variant="muted"
            />

            <Footer />
        </div>
    )
}

export default RSAatCES2026
